export type KaleidoscopeSegment = {
  startAngle: number;
  endAngle: number;
  fill: string;
};

export type KaleidoscopeFacet = {
  cx: number;
  cy: number;
  radius: number;
  rotation: number;
  fill: string;
};

export type KaleidoscopeSpec = {
  segments: KaleidoscopeSegment[];
  facets: KaleidoscopeFacet[];
  ringStroke: string;
};

const MAX_PALETTE_COLORS = 5;

function hashUserId(userId: string): number {
  let hash = 2166136261;

  for (let index = 0; index < userId.length; index += 1) {
    hash ^= userId.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

function createSeededRandom(seed: number): () => number {
  let state = seed >>> 0;

  return () => {
    state = (Math.imul(1664525, state) + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}

function hslColor(hue: number, saturation: number, lightness: number): string {
  const normalizedHue = ((hue % 360) + 360) % 360;
  return `hsl(${normalizedHue.toFixed(1)}, ${saturation.toFixed(1)}%, ${lightness.toFixed(1)}%)`;
}

function buildContrastPalette(seed: number): string[] {
  const rand = createSeededRandom(seed ^ 0x85ebca6b);
  const colorCount = 3 + Math.floor(rand() * (MAX_PALETTE_COLORS - 2)); // 3–5
  const baseHue = rand() * 360;
  const hueStep = 360 / colorCount;
  const hues: number[] = [];

  for (let index = 0; index < colorCount; index += 1) {
    const jitter = (rand() - 0.5) * hueStep * 0.22;
    hues.push((baseHue + index * hueStep + jitter + 360) % 360);
  }

  return hues.map((hue) => {
    const saturation = 76 + rand() * 16; // 76–92%
    const lightness = 60 + rand() * 12; // 60–72%
    return hslColor(hue, saturation, lightness);
  });
}

function pickPaletteColor(palette: string[], seed: number, slot: number): string {
  return palette[(seed + slot) % palette.length];
}

function toDegrees(radians: number): number {
  return (radians * 180) / Math.PI;
}

export function buildKaleidoscopeSpec(userId: string): KaleidoscopeSpec {
  const seed = hashUserId(userId.trim() || 'anonymous');
  const rand = createSeededRandom(seed);
  const palette = buildContrastPalette(seed);
  const foldCount = 6 + Math.floor(rand() * 3); // 6, 7, or 8 wedges
  const wedgeSpan = 360 / foldCount;
  const phase = ((seed % 360) + toDegrees(rand()) * wedgeSpan * 0.35) % wedgeSpan;

  const segments: KaleidoscopeSegment[] = Array.from({ length: foldCount }, (_, index) => ({
    startAngle: phase + index * wedgeSpan,
    endAngle: phase + (index + 1) * wedgeSpan,
    fill: pickPaletteColor(palette, seed, index),
  }));

  const facets: KaleidoscopeFacet[] = Array.from({ length: foldCount }, (_, index) => {
    const midAngle = phase + (index + 0.5) * wedgeSpan;
    const facetRadius = 0.14 + rand() * 0.1;
    const orbit = 0.28 + rand() * 0.14;

    return {
      cx: 0.5 + Math.cos((midAngle * Math.PI) / 180) * orbit,
      cy: 0.5 + Math.sin((midAngle * Math.PI) / 180) * orbit,
      radius: facetRadius,
      rotation: midAngle + (rand() - 0.5) * 36,
      fill: pickPaletteColor(palette, seed, index + 2),
    };
  });

  const ringHue = (seed * 0.618 + rand() * 40) % 360;

  return {
    segments,
    facets,
    ringStroke: hslColor(ringHue, 68, 78),
  };
}

export function describeWedgePath(
  size: number,
  startAngle: number,
  endAngle: number,
): string {
  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2;
  const start = (startAngle * Math.PI) / 180;
  const end = (endAngle * Math.PI) / 180;
  const x1 = cx + radius * Math.cos(start);
  const y1 = cy + radius * Math.sin(start);
  const x2 = cx + radius * Math.cos(end);
  const y2 = cy + radius * Math.sin(end);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;

  return `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
}

export function describeFacetPath(size: number, facet: KaleidoscopeFacet): string {
  const cx = facet.cx * size;
  const cy = facet.cy * size;
  const radius = facet.radius * size;
  const rotation = (facet.rotation * Math.PI) / 180;
  const sides = 6;

  const points = Array.from({ length: sides }, (_, index) => {
    const angle = rotation + ((index * 2 * Math.PI) / sides);
    return {
      x: cx + radius * Math.cos(angle),
      y: cy + radius * Math.sin(angle),
    };
  });

  return `M ${points[0].x} ${points[0].y} ${points.slice(1).map((point) => `L ${point.x} ${point.y}`).join(' ')} Z`;
}
